import {
    calculateTotalRobots,
    distributeRobotsEvenly,
    calculateRobotsMultipliers,
} from '@kaeliux/game-engine';
import {
    BUILDING_NAMES,
    RESOURCE_NAMES,
    ROBOT_MAX_PER_RESOURCE,
    ROBOT_FACILITY_BONUS_PER_LEVEL,
    ROBOT_METAL_BONUS_PER_UNIT,
    ROBOT_MINERAL_BONUS_PER_UNIT,
    ROBOT_GAS_BONUS_PER_UNIT,
} from '@kaeliux/shared';
import { Modal } from './ui/Modal';
import { resourceCostGlyph } from './resourceCostIcons';
import type { ResourceKey } from '../utils/resourceQuantities';
import { Button } from './ui/Button';

export interface RobotProductionModalProps {
    open: boolean;
    robotFactoryLevel: number;
    automationCenterLevel: number;
    onClose: () => void;
}

interface RobotResourceRow {
    label: string;
    resource: ResourceKey;
    count: number;
    bonusPerUnit: number;
    multiplier: number;
}

function formatBonus(multiplier: number): string {
    return `+${Math.round((multiplier - 1) * 100)}%`;
}

export default function RobotProductionModal({
    open,
    robotFactoryLevel,
    automationCenterLevel,
    onClose,
}: RobotProductionModalProps) {
    const totalRobots = calculateTotalRobots(robotFactoryLevel, automationCenterLevel);
    const distribution = distributeRobotsEvenly(totalRobots);
    const multipliers = calculateRobotsMultipliers(distribution);

    const rows: RobotResourceRow[] = [
        { label: RESOURCE_NAMES.metal, resource: 'metal', count: distribution.metal, bonusPerUnit: ROBOT_METAL_BONUS_PER_UNIT, multiplier: multipliers.metal },
        { label: RESOURCE_NAMES.minerals, resource: 'minerals', count: distribution.minerals, bonusPerUnit: ROBOT_MINERAL_BONUS_PER_UNIT, multiplier: multipliers.minerals },
        { label: RESOURCE_NAMES.gas, resource: 'gas', count: distribution.gas, bonusPerUnit: ROBOT_GAS_BONUS_PER_UNIT, multiplier: multipliers.gas },
    ];

    return (
        <Modal open={open} onClose={onClose} title="Production Robots" size="sm">
            <div className="flex flex-col gap-4">
                <p className="text-body text-content-secondary">
                    {BUILDING_NAMES.robotFactory} (Level {robotFactoryLevel}) and {BUILDING_NAMES.automationCenter} (Level {automationCenterLevel}, +{ROBOT_FACILITY_BONUS_PER_LEVEL} per level)
                    together provide <span className="text-content-primary font-semibold">{totalRobots} robot{totalRobots === 1 ? '' : 's'}</span>.
                    Robots are split evenly across {RESOURCE_NAMES.metal}, {RESOURCE_NAMES.minerals} and {RESOURCE_NAMES.gas}, capped at {ROBOT_MAX_PER_RESOURCE} per resource.
                </p>

                <div className="flex flex-col gap-2">
                    {rows.map((row) => (
                        <div key={row.label} className="flex items-center justify-between rounded-tile bg-surface-raised border border-hairline px-3 py-2">
                            <div className="flex items-center gap-2 text-value text-content-secondary">
                                {resourceCostGlyph(row.resource)}
                                <span>{row.label}</span>
                            </div>
                            <div className="text-right">
                                <div className="text-value text-content-primary">
                                    {row.count} / {ROBOT_MAX_PER_RESOURCE} robots
                                </div>
                                <div className="text-micro text-status-success">
                                    {formatBonus(row.multiplier)} production
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex justify-end">
                    <Button variant="secondary" size="md" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
